/**
 * InputSystem - Captures keyboard, gamepad, and mouse input.
 * Covers tasks 7.1 (gamepad), 7.2 (touch), 7.3 (rebindable keys),
 * 7.5 (mouse aiming), 7.8 (auto-pass toggle), 7.9 (through-ball), 7.10 (lob shot),
 * 9.8 (fix keyboard input not registering).
 */

import { SaveSystem } from "./SaveSystem";

export interface GamepadState {
  connected: boolean;
  leftX: number;
  leftY: number;
  rightX: number;
  rightY: number;
  buttons: boolean[];
}

export class InputSystem {
  private _keys = new Set<string>();
  private _gamepad: GamepadState = {
    connected: false,
    leftX: 0,
    leftY: 0,
    rightX: 0,
    rightY: 0,
    buttons: [],
  };
  private _mouseX = 0;
  private _mouseY = 0;
  private _mouseAimEnabled = false;
  private _autoPassEnabled = false;
  private _bindings: Record<string, string> = {};
  private _player2Keys = new Set<string>(); // local multiplayer (5.5)
  private _touchState = { forward: false, backward: false, left: false, right: false, shoot: false };
  private _gamepadIndex = -1;

  // Keys that should prevent default browser behavior
  private static _gameKeys = new Set([
    "KeyW", "KeyA", "KeyS", "KeyD",
    "ShiftLeft", "ShiftRight", "Space",
    "KeyE", "KeyQ", "KeyF", "KeyR", "Escape",
    "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  ]);

  constructor() {
    this._loadBindings();

    // Keyboard events
    window.addEventListener("keydown", (e) => {
      this._keys.add(e.code);
      if (InputSystem._gameKeys.has(e.code)) e.preventDefault();

      // Auto-pass toggle (task 7.8)
      if (e.code === this._bindings["autoPass"]) {
        this._autoPassEnabled = !this._autoPassEnabled;
        console.log("Auto-pass:", this._autoPassEnabled);
      }
    });
    window.addEventListener("keyup", (e) => {
      this._keys.delete(e.code);
      if (InputSystem._gameKeys.has(e.code)) e.preventDefault();
    });

    // Fix: clear keys on window blur (task 9.8)
    window.addEventListener("blur", () => this._keys.clear());
    // Also re-register when window is focused
    window.addEventListener("focus", () => {
      // Keys cleared on blur, so no stuck keys
    });

    // Gamepad support (task 7.1)
    window.addEventListener("gamepadconnected", (e: GamepadEvent) => {
      this._gamepadIndex = e.gamepad.index;
      this._gamepad.connected = true;
      console.log(`Gamepad connected: ${e.gamepad.id}`);
    });
    window.addEventListener("gamepaddisconnected", (e: GamepadEvent) => {
      if (e.gamepad.index === this._gamepadIndex) {
        this._gamepadIndex = -1;
        this._gamepad = {
          connected: false, leftX: 0, leftY: 0,
          rightX: 0, rightY: 0, buttons: [],
        };
      }
    });

    // Mouse aiming (task 7.5)
    window.addEventListener("mousemove", (e) => {
      this._mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      this._mouseY = -(e.clientY / window.innerHeight) * 2 + 1;
    });

    // Touch controls (task 7.2)
    this._setupTouchControls();
  }

  /** Load key bindings from preferences */
  private _loadBindings(): void {
    const prefs = SaveSystem.getPreferences();
    this._bindings = { ...prefs.keyBindings };
    // Add any new keys not in saved bindings
    const defaults: Record<string, string> = {
      forward: "KeyW", backward: "KeyS", left: "KeyA", right: "KeyD",
      sprint: "ShiftLeft", shoot: "Space", throughPass: "KeyE",
      lobKick: "KeyQ", tackle: "KeyF", pause: "Escape", autoPass: "KeyR",
    };
    for (const [action, code] of Object.entries(defaults)) {
      if (!this._bindings[action]) this._bindings[action] = code;
    }
  }

  /** Rebind a key (task 7.3) */
  rebind(action: string, code: string): void {
    this._bindings[action] = code;
    SaveSystem.setKeyBinding(action, code);
    // Add to game keys set so it gets preventDefault
    InputSystem._gameKeys.add(code);
  }

  /** Get current binding for an action */
  getBinding(action: string): string {
    return this._bindings[action] || "";
  }

  /** Get all current bindings */
  getAllBindings(): Record<string, string> {
    return { ...this._bindings };
  }

  /** Poll gamepad state each frame */
  pollGamepad(): void {
    if (this._gamepadIndex < 0) return;
    const gamepads = navigator.getGamepads();
    const gp = gamepads[this._gamepadIndex];
    if (!gp) return;

    const deadzone = 0.15;
    this._gamepad.leftX = Math.abs(gp.axes[0]) > deadzone ? gp.axes[0] : 0;
    this._gamepad.leftY = Math.abs(gp.axes[1]) > deadzone ? gp.axes[1] : 0;
    this._gamepad.rightX = Math.abs(gp.axes[2]) > deadzone ? gp.axes[2] : 0;
    this._gamepad.rightY = Math.abs(gp.axes[3]) > deadzone ? gp.axes[3] : 0;
    this._gamepad.buttons = gp.buttons.map(b => b.pressed);
    this._gamepad.connected = true;
  }

  isDown(code: string): boolean {
    return this._keys.has(code);
  }

  // ─── Movement helpers (keyboard + gamepad + touch) ───────────────

  get forward(): boolean {
    return this.isDown(this._bindings["forward"] || "KeyW")
      || this._gamepad.leftY < -0.3
      || this._touchState.forward;
  }
  get backward(): boolean {
    return this.isDown(this._bindings["backward"] || "KeyS")
      || this._gamepad.leftY > 0.3
      || this._touchState.backward;
  }
  get left(): boolean {
    return this.isDown(this._bindings["left"] || "KeyA")
      || this._gamepad.leftX < -0.3
      || this._touchState.left;
  }
  get right(): boolean {
    return this.isDown(this._bindings["right"] || "KeyD")
      || this._gamepad.leftX > 0.3
      || this._touchState.right;
  }
  get sprint(): boolean {
    const code = this._bindings["sprint"] || "ShiftLeft";
    return this.isDown(code) || this.isDown("ShiftRight")
      || (this._gamepad.buttons[10] ?? false); // L3 button on gamepad
  }
  get shoot(): boolean {
    const code = this._bindings["shoot"] || "Space";
    return this.isDown(code) || (this._gamepad.buttons[0] ?? false) || this._touchState.shoot; // A/X button
  }
  get throughPass(): boolean {
    const code = this._bindings["throughPass"] || "KeyE";
    return this.isDown(code) || (this._gamepad.buttons[2] ?? false); // X/Square
  }
  get lobKick(): boolean {
    const code = this._bindings["lobKick"] || "KeyQ";
    return this.isDown(code) || (this._gamepad.buttons[3] ?? false); // Y/Triangle
  }
  get tackle(): boolean {
    const code = this._bindings["tackle"] || "KeyF";
    return this.isDown(code) || (this._gamepad.buttons[1] ?? false); // B/Circle
  }
  get escape(): boolean {
    return this.isDown(this._bindings["pause"] || "Escape")
      || (this._gamepad.buttons[9] ?? false); // Start button
  }
  get autoPass(): boolean {
    return this._autoPassEnabled;
  }

  // ─── Analog movement (gamepad sticks) ────────────────────────────

  get moveX(): number {
    if (this._gamepad.connected && Math.abs(this._gamepad.leftX) > 0.15) {
      return this._gamepad.leftX;
    }
    return (this.right ? 1 : 0) - (this.left ? 1 : 0);
  }

  get moveZ(): number {
    if (this._gamepad.connected && Math.abs(this._gamepad.leftY) > 0.15) {
      return -this._gamepad.leftY; // Invert Y axis
    }
    return (this.forward ? 1 : 0) - (this.backward ? 1 : 0);
  }

  // ─── Mouse aim (task 7.5) ─────────────────────────────────────────

  get mouseAimEnabled(): boolean { return this._mouseAimEnabled; }
  setMouseAimEnabled(v: boolean): void { this._mouseAimEnabled = v; }
  get mouseX(): number { return this._mouseX; }
  get mouseY(): number { return this._mouseY; }

  // ─── Auto-pass (task 7.8) ─────────────────────────────────────────

  get isAutoPassEnabled(): boolean { return this._autoPassEnabled; }
  setAutoPass(v: boolean): void { this._autoPassEnabled = v; }

  // ─── Gamepad state ────────────────────────────────────────────────

  get gamepadConnected(): boolean { return this._gamepad.connected; }

  // ─── Player 2 controls (local multiplayer 5.5) ───────────────────

  get p2Forward(): boolean { return this._player2Keys.has("ArrowUp"); }
  get p2Backward(): boolean { return this._player2Keys.has("ArrowDown"); }
  get p2Left(): boolean { return this._player2Keys.has("ArrowLeft"); }
  get p2Right(): boolean { return this._player2Keys.has("ArrowRight"); }
  get p2Shoot(): boolean { return this._player2Keys.has("Numpad0"); }
  get p2Sprint(): boolean { return this._player2Keys.has("NumpadEnter"); }

  private _p2KeysSetup = false;
  setupPlayer2(): void {
    if (this._p2KeysSetup) return;
    this._p2KeysSetup = true;
    window.addEventListener("keydown", (e) => {
      if (["ArrowUp","ArrowDown","ArrowLeft","ArrowRight","Numpad0","NumpadEnter"].includes(e.code)) {
        this._player2Keys.add(e.code);
        e.preventDefault();
      }
    });
    window.addEventListener("keyup", (e) => {
      this._player2Keys.delete(e.code);
    });
  }

  // ─── Touch controls (task 7.2) ───────────────────────────────────

  private _setupTouchControls(): void {
    // D-pad style virtual buttons can be added to the HTML
    // These are triggered by external UI elements
    const touchMap: Record<string, keyof typeof this._touchState> = {
      "touch-forward": "forward",
      "touch-backward": "backward",
      "touch-left": "left",
      "touch-right": "right",
      "touch-shoot": "shoot",
    };

    Object.entries(touchMap).forEach(([id, action]) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener("touchstart", (e) => {
        this._touchState[action] = true;
        e.preventDefault();
      }, { passive: false });
      el.addEventListener("touchend", () => {
        this._touchState[action] = false;
      });
    });
  }
}
